import { FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import QRCode from "qrcode";

import {
  PublicKeyCredentialRequestOptionsJSON,
  respondToTotp,
  respondToWebAuthn,
  selectMfaFactor,
} from "../auth/cognito";

// State carried via react-router's location.state from SignInPage.
// Comes in four shapes depending on what /auth/login returned:
//   - mfa_required / mfa_setup: the existing TOTP flows
//   - webauthn_required: passkey assertion
//   - mfa_select: user has multiple factors enrolled, render picker
interface ChallengeState {
  state: "mfa_required" | "mfa_setup" | "webauthn_required" | "mfa_select";
  session: string;
  email: string;
  // Only set when state="mfa_setup" — the TOTP secret to render as a
  // QR code in the user's authenticator app.
  secretCode?: string;
  // Only set when state="webauthn_required" — the
  // PublicKeyCredentialRequestOptionsJSON the SPA hands to navigator.
  // credentials.get() via @simplewebauthn/browser.
  credentialRequestOptions?: PublicKeyCredentialRequestOptionsJSON;
  // Only set when state="mfa_select" — Cognito's list of factors the
  // user can choose from. Values: WEB_AUTHN, SOFTWARE_TOKEN_MFA.
  availableFactors?: string[];
}

export function MfaChallengePage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Initial challenge from location.state. We hold it in component
  // state so a SELECT_CHALLENGE → SOFTWARE_TOKEN_MFA / WEB_AUTHN
  // transition can replace it without a navigation hop.
  const initial = location.state as ChallengeState | undefined;
  const [challenge, setChallenge] = useState<ChallengeState | undefined>(initial);

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // QR is rendered to a base64-encoded PNG data URL and shown via
  // <img src="data:...">. CSP `img-src 'self' data:` already permits
  // this; we deliberately avoid dangerouslySetInnerHTML so there's no
  // path for an HTML payload to land in the DOM.
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!challenge || challenge.state !== "mfa_setup" || !challenge.secretCode) {
      setQrDataUrl(null);
      return;
    }
    const issuer = "RIoT-FOTA";
    const label = encodeURIComponent(`${issuer}:${challenge.email}`);
    const otpauth = `otpauth://totp/${label}?secret=${challenge.secretCode}&issuer=${encodeURIComponent(issuer)}`;
    QRCode.toDataURL(otpauth, { margin: 1, width: 200, errorCorrectionLevel: "M" })
      .then((url) => setQrDataUrl(url))
      .catch((e) => {
        // Falls back to plain-text secret display below if QR fails.
        // eslint-disable-next-line no-console
        console.warn("QR render failed", e);
        setQrDataUrl(null);
      });
  }, [challenge]);

  // No challenge in location.state → user navigated here directly.
  // Send them back to sign-in.
  useEffect(() => {
    if (!challenge) navigate("/sign-in", { replace: true });
  }, [challenge, navigate]);

  if (!challenge) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!challenge || challenge.state === "webauthn_required" || challenge.state === "mfa_select") return;
    setError(null);
    setLoading(true);
    try {
      const resp = await respondToTotp({
        state: challenge.state,
        session: challenge.session,
        email: challenge.email,
        code,
      });
      if (resp.state === "authenticated") {
        navigate("/", { replace: true });
        return;
      }
      setError("Additional verification required — please sign in again");
    } catch (err) {
      setError(err instanceof Error ? err.message : "MFA verification failed");
    } finally {
      setLoading(false);
    }
  }

  // Passkey flow: kick the WebAuthn ceremony as soon as the user
  // clicks "Use your passkey" (a discrete user-gesture is required by
  // browsers — we don't auto-fire on mount). On success we land at /
  // identical to a successful TOTP step-up.
  async function onPasskeyVerify() {
    if (!challenge || challenge.state !== "webauthn_required" || !challenge.credentialRequestOptions) return;
    setError(null);
    setLoading(true);
    try {
      const credential = await startAuthentication({
        optionsJSON: challenge.credentialRequestOptions,
      });
      const resp = await respondToWebAuthn({
        session: challenge.session,
        email: challenge.email,
        credential,
      });
      if (resp.state === "authenticated") {
        navigate("/", { replace: true });
        return;
      }
      // Cognito returned a non-terminal state from the verify call —
      // unexpected; bounce to /sign-in for a clean restart.
      navigate("/sign-in", { replace: true });
    } catch (err) {
      // NotAllowedError is what browsers throw when the user cancels
      // the platform passkey UI — surface a friendlier message and
      // let the user try again without leaving the page.
      if (err instanceof Error && err.name === "NotAllowedError") {
        setError("Passkey prompt was cancelled");
        return;
      }
      // Anything else is most likely a session-expired / bad-assertion
      // 401 from /auth/webauthn/authentication/verify. The Cognito
      // session has a ~3-min TTL and is one-shot — there's no
      // retrying without restarting the sign-in. Bounce to /sign-in
      // with a friendly message stashed for the page to show.
      const message = err instanceof Error ? err.message : "Passkey verification failed";
      navigate("/sign-in", { replace: true, state: { signInError: message } });
    } finally {
      setLoading(false);
    }
  }

  // mfa_select: Cognito wants the user to pick a factor. We render
  // a passkey-default picker. Selecting a factor calls /auth/mfa/select
  // and replaces the challenge state with the result (mfa_required /
  // webauthn_required), at which point the page re-renders with the
  // existing branches.
  async function onSelectFactor(factor: "WEB_AUTHN" | "SOFTWARE_TOKEN_MFA") {
    if (!challenge || challenge.state !== "mfa_select") return;
    setError(null);
    setLoading(true);
    try {
      const resp = await selectMfaFactor({
        session: challenge.session,
        email: challenge.email,
        factor,
      });
      if (resp.state === "webauthn_required") {
        setChallenge({
          state: "webauthn_required",
          session: resp.session,
          email: challenge.email,
          credentialRequestOptions: resp.credentialRequestOptions,
        });
      } else if (resp.state === "mfa_required") {
        setChallenge({
          state: "mfa_required",
          session: resp.session,
          email: challenge.email,
        });
      } else {
        navigate("/sign-in", {
          replace: true,
          state: { signInError: "Unexpected factor response — please sign in again." },
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not select MFA factor");
    } finally {
      setLoading(false);
    }
  }

  const isSelect = challenge.state === "mfa_select";
  const isSetup = challenge.state === "mfa_setup";
  const isWebAuthn = challenge.state === "webauthn_required";
  const factors = challenge.availableFactors ?? [];
  const hasPasskey = factors.includes("WEB_AUTHN");
  const hasTOTP = factors.includes("SOFTWARE_TOKEN_MFA");

  return (
    <div className="flex min-h-screen items-center justify-center bg-riot-navy-950 px-6 py-10">
      <div className="w-full max-w-md rounded-[28px] border border-white/12 bg-riot-navy-900/80 px-8 py-10 shadow-[0_28px_90px_rgba(2,8,23,0.55)] backdrop-blur-md">
        <h1 className="mb-2 text-2xl font-bold text-white">
          {isSelect
            ? "Verify your sign-in"
            : isWebAuthn
              ? "Verify with your passkey"
              : isSetup
                ? "Set up two-factor authentication"
                : "Enter your verification code"}
        </h1>
        <p className="mb-6 text-sm text-riot-navy-200/80">
          {isSelect
            ? "Choose how you'd like to verify your sign-in. We recommend using your passkey."
            : isWebAuthn
              ? "Confirm the prompt from your device (Touch ID, Face ID, Windows Hello, or your security key) to finish signing in."
              : isSetup
                ? "Scan the QR code below with your authenticator app (Google Authenticator, 1Password, Authy, etc.), then enter the 6-digit code it shows."
                : "Open your authenticator app and enter the 6-digit code for RIoT-FOTA."}
        </p>

        {isSelect && (
          <div className="space-y-3">
            {!hasPasskey && !hasTOTP && (
              <>
                <p role="alert" className="text-sm text-riot-navy-100">
                  This sign-in session cannot continue. Return to sign-in and enter your email and password again.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/sign-in", { replace: true })}
                  className="w-full rounded-xl bg-riot-gold-400 px-4 py-2.5 text-sm font-semibold text-riot-navy-950"
                >
                  Return to sign-in
                </button>
              </>
            )}
            {error && (
              <div role="alert" className="rounded-xl border border-red-500/25 bg-red-950/35 px-3 py-2 text-xs text-red-200">
                {error}
              </div>
            )}
            {hasPasskey && (
              <button
                type="button"
                onClick={() => onSelectFactor("WEB_AUTHN")}
                disabled={loading}
                className="w-full rounded-xl bg-riot-gold-400 px-4 py-2.5 text-sm font-semibold text-riot-navy-950 transition hover:bg-riot-gold-300 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Loading…" : "Use my passkey"}
              </button>
            )}
            {hasTOTP && (
              <button
                type="button"
                onClick={() => onSelectFactor("SOFTWARE_TOKEN_MFA")}
                disabled={loading}
                className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-medium text-riot-navy-100 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Use authenticator app instead
              </button>
            )}
          </div>
        )}

        {isWebAuthn && (
          <div className="space-y-4">
            {error && (
              <div role="alert" className="rounded-xl border border-red-500/25 bg-red-950/35 px-3 py-2 text-xs text-red-200">
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={onPasskeyVerify}
              disabled={loading}
              className="w-full rounded-xl bg-riot-gold-400 px-4 py-2.5 text-sm font-semibold text-riot-navy-950 transition hover:bg-riot-gold-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Waiting for your device…" : "Use your passkey"}
            </button>
          </div>
        )}

        {!isWebAuthn && !isSelect && (
          <>
            {isSetup && qrDataUrl && (
              <div className="mb-4 flex justify-center rounded-xl bg-white p-4">
                <img src={qrDataUrl} alt="MFA setup QR code" width={200} height={200} />
              </div>
            )}
            {isSetup && challenge.secretCode && (
              <div className="mb-4 text-center text-[11px] text-riot-navy-300">
                Or enter this secret manually:&nbsp;
                <code className="rounded bg-riot-navy-800 px-2 py-0.5 text-riot-navy-100">{challenge.secretCode}</code>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="mfa-code" className="mb-1 block text-xs font-medium text-riot-navy-100/90">
                  6-digit code
                </label>
                <input
                  id="mfa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                  placeholder="000000"
                  className="w-full rounded-xl border border-white/10 bg-white/6 px-3 py-2.5 text-center font-mono text-lg tracking-[0.4em] text-white placeholder:text-riot-navy-300/70 focus:border-riot-gold-400/70 focus:outline-none focus:ring-2 focus:ring-riot-gold-400/30"
                />
              </div>

              {error && (
                <div role="alert" className="rounded-xl border border-red-500/25 bg-red-950/35 px-3 py-2 text-xs text-red-200">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full rounded-xl bg-riot-gold-400 px-4 py-2.5 text-sm font-semibold text-riot-navy-950 transition hover:bg-riot-gold-300 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Verifying..." : isSetup ? "Verify & finish setup" : "Verify"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
